import BigNumber from "bignumber.js";
import { formatNumber, Solana, TokenProgram } from "@glow/shared";

export const solAmountString = (
  amount: number | string | BigNumber,
): string => {
  const sol = new BigNumber(amount);
  if (sol.isNaN()) {
    return "";
  }

  return formatNumber({ num: sol.toNumber() }) + " SOL";
};

export const solStringFromLamport = (
  lamport: string | number | BigNumber,
): string => {
  const sol = new BigNumber(lamport).dividedBy(Solana.LAMPORTS_PER_SOL);
  return solAmountString(sol);
};

export const usdFromSol = ({
  price_usd,
  maximumFractionDigits = 2,
  ...params
}: {
  price_usd: BigNumber | number | string;
  maximumFractionDigits?: number;
} & (
  | {
      lamports: BigNumber | number | string;
    }
  | {
      sol: BigNumber | number;
    }
)): string => {
  let lamports;
  if ("lamports" in params) {
    lamports = params.lamports;
  } else {
    lamports = new BigNumber(params.sol).times(Solana.LAMPORTS_PER_SOL);
  }

  const sol = new BigNumber(lamports).dividedBy(Solana.LAMPORTS_PER_SOL);
  const usd = new BigNumber(price_usd).multipliedBy(sol);
  return "$" + usd.toFormat(maximumFractionDigits);
};

export const unitsWithDecimals = ({
  units,
  decimals,
}: {
  units: BigNumber | string | number;
  decimals: number;
}): BigNumber => {
  return new BigNumber(units).div(Math.pow(10, decimals));
};

export const usdFromUnits = ({
  price_usd,
  units,
  decimals,
  maximumFractionDigits = 2,
}: {
  price_usd: string;
  units: string;
  decimals: number;
  maximumFractionDigits?: number;
}): string => {
  const amt = unitsWithDecimals({ units, decimals });
  const usd = new BigNumber(price_usd).multipliedBy(amt);
  return "$" + usd.toFormat(maximumFractionDigits);
};

export const usdFromCoin = ({
  coin,
  ...props
}: {
  coin: TokenProgram.CoinInfo;
} & (
  | {
      units: BigNumber | string | number;
    }
  | {
      fractionalUnits: BigNumber | number;
    }
)): string | null => {
  const price_usd_str = coin.price_usd;
  if (!price_usd_str) {
    return null;
  }

  let units;
  if ("units" in props) {
    units = props.units;
  } else {
    units = new BigNumber(props.fractionalUnits).times(10 ** coin.decimals);
  }

  const amt = unitsWithDecimals({
    units,
    decimals: coin.decimals,
  });
  const usd = new BigNumber(price_usd_str).multipliedBy(amt);
  return "$" + usd.toFormat(2);
};
